import {
  Data,
  PropertyMap,
  FormulaPropertyDescriptor,
  LookupPropertyDescriptor,
  Workbook,
  PropertyDescriptorType,
  PropertySet,
  ObjectKey,
  isEditablePropertyType,
} from "../core/Workbook";
import { parse } from "../parsers/formula.js";

export const getValue = (
  objectKey: ObjectKey,
  propertyKey: string,
  data: Data,
  properties: PropertySet,
  workbook: Workbook
): string => {
  return isExplicit(propertyKey, properties.descriptors)
    ? getExplicitValue(objectKey, propertyKey, data)
    : getCalculatedValue(objectKey, propertyKey, data, properties, workbook);
};
const isExplicit = (propertyKey: string, descriptors: PropertyMap): boolean => {
  if (!(propertyKey in descriptors)) {
    // TODO: this column is unpopulated by definition so optimize setting value directly to ""
    return true;
  }
  return isEditablePropertyType(descriptors[propertyKey].type);
};
const getCalculatedValue = (
  objectKey: ObjectKey,
  propertyKey: string,
  data: Data,
  properties: PropertySet,
  workbook: Workbook
): string => {
  const descriptor = properties.descriptors[propertyKey];
  // Handle this differently depending on column type
  switch (descriptor.type) {
    case PropertyDescriptorType.Formula:
      return getFormulaValue(
        objectKey,
        data,
        descriptor as FormulaPropertyDescriptor,
        properties,
        workbook
      );
    case PropertyDescriptorType.Lookup:
      return getLookupValue(
        objectKey,
        data,
        descriptor as LookupPropertyDescriptor,
        properties,
        workbook
      );
    default:
      console.log("Row Value Error: Type not found:", descriptor.type);
      return "#NaN"; // TODO: Better error reporting
  }
};
const getFormulaValue = (
  objectKey: ObjectKey,
  data: Data,
  properties: FormulaPropertyDescriptor,
  propertySet: PropertySet,
  workbook: Workbook
): string => {
  let value = "";
  // Let's assume that the type is formula, and the parameter is the expression
  // We can assume that propertyKey is in properties, otherwise row value would be explicit
  const expression = properties.expression;
  if (expression) {
    try {
      value = parse(expression, {
        // type: "evaluate",
        getRowValue: (propertyKey: string) =>
          getValue(objectKey, propertyKey, data, propertySet, workbook),
      });
    } catch (e) {
      console.log("Error parsing: ", expression);
      console.log(e);
      return "#NaN";
    }
    return value;
  } else {
    console.debug(
      "Row Value Error: Formula type without expression: ",
      objectKey,
      properties
    );
    return "#NaN";
  }
};
const getLookupValue = (
  objectKey: ObjectKey,
  data: Data,
  properties: LookupPropertyDescriptor,
  propertySet: PropertySet,
  workbook: Workbook
): string => {
  let value = "";
  // We need to find the reference dataset in the operational memory, and load it in if its not already here
  // For the moment, we can assume it is a sheet in the current workbook
  const referenceSheetId = properties.referenceSheet;

  const referenceSheet = workbook.data.datasets[referenceSheetId];
  const lookupKey = getValue(
    objectKey,
    properties.keyColumnInCurrentSheet,
    data,
    propertySet,
    workbook
  );
  // We may not have any data in this row
  if (lookupKey === "" || lookupKey === "#NaN") {
    return "";
  }
  // Find row
  let referenceObjectKey;
  for (const objectKey of Object.keys(referenceSheet.data)) {
    referenceObjectKey = objectKey;
    const referenceValue = getValue(
      referenceObjectKey,
      properties.keyColumnInReferenceSheet,
      referenceSheet.data,
      referenceSheet.properties,
      workbook
    );
    if (referenceValue === lookupKey) {
      break;
    }
  }
  try {
    value =
      referenceObjectKey !== undefined
        ? getValue(
            referenceObjectKey,
            properties.referenceColumnInReferenceSheet,
            referenceSheet.data,
            referenceSheet.properties,
            workbook
          )
        : "#NaN"; // TODO: Lookup
  } catch (e) {
    console.log("Error parsing: ", referenceSheet);
    console.log(e);
    return "#NaN";
  }
  return value;
};
export const getExplicitValue = (
  objectKey: ObjectKey | undefined,
  propertyKey: string,
  data: Data
): string => {
  let value = "";
  if (
    objectKey !== undefined &&
    objectKey in data &&
    propertyKey in data[objectKey]
  ) {
    value = data[objectKey][propertyKey];
  }
  return value;
};
